/**
 * `useModelView(conversationId)` — THIN SHIM over `useConversationViews`
 * (builder-ssot-consolidation Phase 5 Session 5.2; ADR 0017).
 *
 * This hook NO LONGER hits `/model-view`. It reads `useConversationViews(id)`
 * (which calls `GET /api/conversations/:id/views`) and adapts the un-enveloped
 * `ViewsResponse` into the legacy `ModelViewPayload` discriminated union that
 * consumers narrow on (`layoutMissing`). The legacy return shape is preserved
 * byte-stable so existing consumers (<ModelLayout>, useSignalFieldResolver,
 * AssumptionIntegrity) keep working without modification. ADR 0017.
 *
 * Adapter reconstitutes:
 *   - `inputValues` (legacy value-map) ← Object.fromEntries(inputs[k].value)
 *   - `provenance` (legacy provenance map) ← views.inputs records (ADR 0018)
 *   - `cellValues` ← views.cellValues (pass-through; key format unchanged)
 *   - `layoutMissing` discriminator ← views.modelLayout == null
 *   - `modelLayout`/`layout` ← views.modelLayout (when present)
 *   - `schema` ← synthesised subset for back-compat consumers
 *   - `confirmedRevision` ← views.meta.revision
 *
 * IMPORTANT-3 (AssumptionIntegrity): consumers read `modelView.data.provenance`
 * NOT `.inputProvenance` — this adapter uses the `.provenance` field name.
 *
 * 410 `MODEL_VERSION_REMOVED` from `getViews` propagates as a thrown
 * `MissingModelError` via `useConversationViews` → TanStack `error` field.
 */
import { useMemo } from 'react';
import type { ModelViewPayload } from '@/lib/api';
import type { ViewsResponse } from '@arcanum-hq/shared-types';
import { useConversationViews } from './use-conversation-views';
import { useModel } from './use-models-v2';

export function useModelView(conversationId: string | null) {
  const views = useConversationViews(conversationId);
  // builder-ssot-consolidation P7 walk-fix (2026-05-21): the `/views` projection
  // is lean and does NOT carry the full ModelSchema (inputs[]/outputs[]/
  // allNamedRanges[]/dependencyGraph[]). The TreeNavigator + CellDetailPanel +
  // deep-link resolver in <ModelLayout> all read `data.schema.{inputs,outputs,
  // allNamedRanges}` — the ADR-0017 shim hardcoded EMPTY arrays here, which
  // silently emptied the Model-tab Navigator. Re-fetch the registry schema by
  // the conversation's modelId (additive-only versioning → latest schema's
  // named set is a superset of any pinned version) and populate `schema`.
  const model = useModel(views.data?.modelId ?? null);

  const adapted = useMemo<ModelViewPayload | undefined>(() => {
    const data = views.data;
    if (!data) return undefined;

    // Resolved registry schema (empty until `useModel` loads — the Navigator
    // simply has no leaves for that brief window, same as the prior behavior).
    // `useModel().data` is the `{success,data,meta}` envelope (api.get does NOT
    // unwrap — same access pattern as builder.tsx `modelDetail.data.outputs`);
    // the ModelSchema lives at `.data`.
    const m = (model.data as { data?: unknown } | undefined)?.data as
      | {
          inputs?: unknown[];
          outputs?: unknown[];
          dependencyGraph?: unknown[];
          tableInputs?: unknown[];
          allNamedRanges?: unknown[];
        }
      | undefined;
    const resolvedSchema = {
      inputs: m?.inputs ?? [],
      outputs: m?.outputs ?? [],
      dependencyGraph: m?.dependencyGraph ?? [],
      tableInputs: m?.tableInputs ?? [],
      allNamedRanges: m?.allNamedRanges ?? [],
    };

    // Reconstitute the legacy value-map from inputs[k].value (ADR 0018).
    // Null-guard: value is z.any() — preserve existing null/format guards.
    const inputValues: Record<string, unknown> = Object.fromEntries(
      Object.entries(data.inputs).map(([k, r]) => [k, r.value])
    );

    // Reconstitute the legacy provenance map — the inputs records ARE the
    // provenance records (ADR 0018). Field name is `.provenance` (NOT
    // `.inputProvenance`) because AssumptionIntegrity.tsx:59 reads
    // `modelView.data?.provenance` verbatim (D2 / RE-POINT #5).
    const provenance: Record<string, unknown> = data.inputs;

    if (data.modelLayout == null) {
      // Layout-missing branch — layoutMissing: true discriminant.
      return {
        layout: null,
        layoutMissing: true as const,
        modelId: data.modelId,
        modelVersion: data.modelVersion,
        latestVersionWithLayout: null,
        cellValues: data.cellValues as Record<string, unknown>,
        inputValues,
        provenance,
        schema: resolvedSchema,
        confirmedRevision: data.meta.revision,
      };
    }

    // Happy-path branch — full renderable payload.
    return {
      layout: data.modelLayout,
      layoutMissing: false as const,
      cellValues: data.cellValues as Record<string, unknown>,
      inputValues,
      provenance,
      schema: resolvedSchema,
      confirmedRevision: data.meta.revision,
    };
  }, [views.data, model.data]);

  // Return a UseQueryResult-compatible shape with the adapted data.
  // We spread the views query result and override `data` with the adapted
  // ModelViewPayload so TanStack Query's isLoading/isError/refetch etc.
  // all work correctly for callers.
  return {
    ...views,
    data: adapted,
  } as Omit<typeof views, 'data'> & { data: ModelViewPayload | undefined };
}
